// File: src/components/NoRegistrationBottomSheet.jsx
import React, { useEffect, useRef } from 'react';
import { LuTriangleAlert, LuX } from 'react-icons/lu';

const isPWA = () =>
  (window.matchMedia?.('(display-mode: standalone)').matches) ||
  window.navigator.standalone === true;

/**
 * EventAlreadyStartedBottomSheet
 * Matches the visual language of LogoutBottomSheet / EndMatchBottomSheet:
 * - dimmed backdrop
 * - top pill + close button
 * - centered LU alert icon
 * - bold title + supportive copy
 * - single primary CTA
 */
export function EventAlreadyStartedBottomSheet({
  eventName = '(Event)',
  onDismiss,
  ctaLabel = 'Find Other Events',
}) {
  const scrollYRef = useRef(0);

  // Freeze + status bar tint (same behavior as other sheets)
  useEffect(() => {
    const meta = document.querySelector('meta[name="theme-color"]');
    scrollYRef.current = window.scrollY || window.pageYOffset;

    // lock page at current scroll
    document.body.style.top = `-${scrollYRef.current}px`;
    document.body.classList.add('overlay-active');
    document.documentElement.classList.add('overlay-active');
    document.getElementById('root')?.classList.add('overlay-active');
    if (meta) meta.content = '#999998';

    return () => {
      const y = scrollYRef.current;
      document.body.classList.remove('overlay-active');
      document.documentElement.classList.remove('overlay-active');
      document.getElementById('root')?.classList.remove('overlay-active');
      document.body.style.top = '';
      window.scrollTo(0, y);
      const m = document.querySelector('meta[name="theme-color"]');
      if (m) m.content = '#ffffff';
    };
  }, []);

  const handleClose = () => onDismiss?.();
  const stop = (e) => e.stopPropagation();

  return (
    <div style={S.backdrop} onClick={handleClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Event already started"
        style={{
          ...S.sheet,
          paddingBottom: `calc(12px + env(safe-area-inset-bottom,0px) + ${isPWA() ? 8 : 0}px)`,
        }}
        onClick={stop}
      >
        {/* Top bar */}
        <div style={S.topBar}>
          <div style={S.pill}>Notice</div>
          <button aria-label="Close" style={S.iconBtn} onClick={handleClose}>
            <LuX size={18} style={{ display: 'block' }} />
          </button>
        </div>

        {/* Icon + copy */}
        <div style={S.hero}>
          <div style={S.iconWrap}>
            <LuTriangleAlert size={28} />
          </div>
          <h3 style={S.title}>Event already started</h3>
          <p style={S.desc}>
            <strong>{eventName}</strong> has already started, and you don’t have a ticket
            for this event. Explore other events you can join.
          </p>
        </div>

        {/* CTA row (single button) */}
        <div style={S.ctaRow}>
          <button type="button" onClick={handleClose} style={S.primaryBtn}>
            {ctaLabel}
          </button>
        </div>

        <style>{`
          @keyframes slideUp { from { transform: translateY(12px); opacity: 0 } to { transform: translateY(0); opacity: 1 } }
          @media (prefers-reduced-motion: reduce) { * { animation: none !important; transition: none !important } }
          button:focus-visible { outline: 0; box-shadow: 0 0 0 3px rgba(59,130,246,.45) }
        `}</style>
      </div>
    </div>
  );
}

/* ───────────────────────── Styles (aligned with Logout/EndMatch sheets) */
const S = {
  backdrop: {
    position: 'fixed', inset: 0,
    background: 'rgba(0,0,0,0.40)',
    zIndex: 9999,
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
    padding: 8,
    fontFamily: "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
  },
  sheet: {
    width: '100%',
    maxWidth: 440,
    background: '#ffffff',
    borderRadius: 12,
    boxShadow: '0 8px 28px rgba(0,0,0,0.25)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    animation: 'slideUp .22s ease-out',
    padding: 16,
    minHeight: 'clamp(320px, 38vh, 520px)',
  },
  topBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: 40,
  },
  pill: {
    background: '#f3f4f6',
    border: '1px solid #e5e7eb',
    color: '#0b0b0b',
    borderRadius: 999,
    padding: '6px 10px',
    fontSize: 12,
    fontWeight: 800,
  },
  iconBtn: {
    background: 'transparent',
    border: '1px solid #e5e7eb',
    width: 32, height: 32,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 0,
    padding: 0,
    borderRadius: 8,
    color: '#0b0b0b',
    cursor: 'pointer',
  },
  hero: {
    textAlign: 'center',
    padding: '16px 8px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
  },
  iconWrap: {
    width: 56, height: 56, borderRadius: 999,
    margin: '6px auto 10px',
    display: 'grid', placeItems: 'center',
    background: '#FEF2F2',
    color: '#B91C1C',
    border: '1px solid #FEE2E2',
  },
  title: {
    margin: 0,
    fontSize: 18,
    lineHeight: 1.25,
    fontWeight: 900,
    color: '#0b0b0b',
  },
  desc: {
    margin: '6px auto 0',
    fontSize: 14,
    lineHeight: 1.5,
    color: '#4b5563',
    maxWidth: 360,
  },
  ctaRow: {
    display: 'flex',
    gap: 8,
    marginTop: 'auto',
  },
  primaryBtn: {
    flex: 1,
    background: '#0b0b0b',
    color: '#ffffff',
    border: '1px solid #0b0b0b',
    borderRadius: 10,
    padding: '12px 14px',
    fontSize: 13,
    fontWeight: 900,
    cursor: 'pointer',
  },
};
